import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api from "@/lib/api";
import { RichTextView } from "@/components/forms/RichTextEditor";
import { PersonLink } from "@/components/groups/PersonLink";
import { GroupReportDetail } from "@/components/groups/GroupReportDetail";

const STATUSES = [
  { value: "pending", label: "Open" },
  { value: "reviewed", label: "Reviewed" },
  { value: "actioned", label: "Actioned" },
  { value: "dismissed", label: "Dismissed" },
  { value: "all", label: "All" },
];

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return ""; } };

/** Phase 7 — Group Management → Moderation.
 *
 * The organizer-facing moderation queue. The server sends an ALLOWLISTED payload, so there
 * is deliberately nothing here that could render a reporter's identity, a platform note or
 * a private Member Message: those fields never arrive. */
export function GroupModerationPanel({ groupId, initialReportId = null }) {
  const [status, setStatus] = React.useState("pending");
  const [reason, setReason] = React.useState("all");
  const [view, setView] = React.useState("reports");
  // A moderation notification deep-links straight to one report. The server re-checks the
  // caller's CURRENT Group role and the report's visibility, so a stale link from a former
  // organizer simply fails — and a resolved report opens showing its current status.
  const [openReport, setOpenReport] = React.useState(initialReportId);
  const qc = useQueryClient();

  const reports = useQuery({
    queryKey: ["group-moderation", groupId, status, reason],
    queryFn: async () => (await api.get(`/groups/${groupId}/moderation/reports`, {
      params: { status, ...(reason !== "all" ? { reason } : {}) },
    })).data,
  });

  const history = useQuery({
    queryKey: ["group-moderation-history", groupId],
    queryFn: async () => (await api.get(`/groups/${groupId}/moderation/history`)).data,
    enabled: view === "history",
  });

  const act = useMutation({
    mutationFn: async ({ reportId, action }) =>
      (await api.post(`/groups/${groupId}/moderation/reports/${reportId}/action`, { action })).data,
    onSuccess: (_d, vars) => {
      toast.success(vars.action === "remove" ? "Content removed from the group."
        : vars.action === "dismiss" ? "Report dismissed." : "Report resolved.");
      qc.invalidateQueries({ queryKey: ["group-moderation", groupId] });
      qc.invalidateQueries({ queryKey: ["group-moderation-counts", groupId] });
      qc.invalidateQueries({ queryKey: ["group-moderation-history", groupId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "That didn't work."),
  });

  const restore = useMutation({
    mutationFn: async ({ contentType, contentId }) => {
      const kind = contentType === "comment" ? "comments" : "posts";
      return (await api.post(`/groups/${groupId}/moderation/${kind}/${contentId}/restore`)).data;
    },
    onSuccess: () => {
      toast.success("Content restored.");
      qc.invalidateQueries({ queryKey: ["group-moderation", groupId] });
      qc.invalidateQueries({ queryKey: ["group-moderation-counts", groupId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "That didn't work."),
  });

  const items = reports.data?.items || [];
  const counts = reports.data?.counts || {};

  return (
    <div className="space-y-4" data-testid="group-moderation-panel">
      <div className="flex flex-wrap items-center gap-2">
        <Tabs value={view} onValueChange={setView}>
          <TabsList data-testid="group-moderation-views">
            <TabsTrigger value="reports" data-testid="group-moderation-view-reports">Reports</TabsTrigger>
            <TabsTrigger value="history" data-testid="group-moderation-view-history">History</TabsTrigger>
          </TabsList>
        </Tabs>
        {counts.pending ? (
          <Badge variant="secondary" data-testid="group-moderation-open-count">
            {counts.pending} open
          </Badge>
        ) : null}
      </div>

      {view === "reports" ? (
        <>
          <div className="flex flex-wrap gap-2">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-[160px]" data-testid="group-moderation-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="w-[200px]" data-testid="group-moderation-reason-filter">
                <SelectValue placeholder="All reasons" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All reasons</SelectItem>
                {(reports.data?.reasons || []).map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {reports.isLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : !items.length ? (
            <Card className="p-6 text-center text-sm text-muted-foreground"
                  data-testid="group-moderation-empty">
              Nothing to review. Reported posts and comments in this group show up here.
            </Card>
          ) : (
            <ul className="space-y-3">
              {items.map((it) => (
                <Card key={it.id} className="space-y-3 p-4" data-testid={`group-report-${it.id}`}>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <Badge variant="outline" data-testid={`group-report-reason-${it.id}`}>{it.reason_label}</Badge>
                    <Badge variant="secondary">{it.content_type}</Badge>
                    <span>{fmt(it.created_at)}</span>
                    {it.platform_held ? (
                      <Badge variant="destructive" className="gap-1" data-testid={`group-report-platform-${it.id}`}>
                        <ShieldAlert className="h-3 w-3" /> Held by Startup Otter
                      </Badge>
                    ) : it.content_removed ? (
                      <Badge variant="secondary" data-testid={`group-report-removed-${it.id}`}>Removed</Badge>
                    ) : null}
                  </div>
                  {it.author ? (
                    <div className="text-sm">
                      <PersonLink person={it.author} testId={`group-report-author-${it.id}`} />
                    </div>
                  ) : null}
                  <div className="text-sm" data-testid={`group-report-body-${it.id}`}>
                    {it.content_exists
                      ? (it.content_body && it.content_body !== "<p></p>"
                          ? <RichTextView html={it.content_body} />
                          : <span className="italic text-muted-foreground">No text.</span>)
                      : <span className="italic text-muted-foreground">This content is no longer available.</span>}
                  </div>
                  {it.details ? (
                    <p className="rounded-md bg-muted/50 p-2 text-xs text-muted-foreground">
                      Reporter note: {it.details}
                    </p>
                  ) : null}
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="default" disabled={act.isPending}
                            data-testid={`group-report-review-${it.id}`}
                            onClick={() => setOpenReport(it.id)}>
                      Review
                    </Button>
                    {!it.content_removed && !it.platform_held ? (
                      <Button size="sm" variant="destructive" disabled={act.isPending}
                              data-testid={`group-report-remove-${it.id}`}
                              onClick={() => act.mutate({ reportId: it.id, action: "remove" })}>
                        Remove from group
                      </Button>
                    ) : null}
                    {it.content_removed && !it.platform_held ? (
                      <Button size="sm" variant="outline" disabled={restore.isPending}
                              data-testid={`group-report-restore-${it.id}`}
                              onClick={() => restore.mutate({ contentType: it.content_type, contentId: it.content_id })}>
                        {it.group_action === "hide" ? "Unhide" : "Restore"}
                      </Button>
                    ) : null}
                    {it.review_status === "pending" ? (
                      <Button size="sm" variant="ghost" disabled={act.isPending}
                              data-testid={`group-report-dismiss-${it.id}`}
                              onClick={() => act.mutate({ reportId: it.id, action: "dismiss" })}>
                        Dismiss
                      </Button>
                    ) : (
                      <Badge variant="outline" data-testid={`group-report-status-${it.id}`}>
                        {it.review_status}
                      </Badge>
                    )}
                  </div>
                </Card>
              ))}
            </ul>
          )}
          <p className="text-xs text-muted-foreground">
            Reports sent to Startup Otter are reviewed separately and aren&apos;t shown here.
            Your decisions apply to this group only.
          </p>
        </>
      ) : (
        <div className="space-y-2" data-testid="group-moderation-history">
          {history.isLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : !(history.data?.entries || []).length ? (
            <Card className="p-6 text-center text-sm text-muted-foreground"
                  data-testid="group-moderation-history-empty">
              No moderation activity yet.
            </Card>
          ) : (
            (history.data.entries).map((e) => (
              <Card key={e.id} className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm"
                    data-testid={`group-moderation-history-${e.id}`}>
                <span className="font-medium">{e.action.replace(/[._]/g, " ")}</span>
                <span className="flex items-center gap-2 text-xs text-muted-foreground">
                  {e.actor ? <PersonLink person={e.actor} /> : null}
                  {fmt(e.created_at)}
                </span>
              </Card>
            ))
          )}
        </div>
      )}
      {openReport ? (
        <GroupReportDetail groupId={groupId} reportId={openReport}
                           onClose={() => setOpenReport(null)} />
      ) : null}
    </div>
  );
}
